import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid, StageDivider } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Accounts & Keys',
    tagline: 'Two kinds of accounts, one cryptographic identity system.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - the two account types and what distinguishes them
//   Q1 - how a private key becomes an address
//   Q2 - what the nonce actually prevents
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 2 of 8" title="Two account types, one address format">
            <Prose>
                Everything on Ethereum happens between <strong>accounts</strong>. An address (that
                <code> 0x</code>-prefixed 40-character hex string) always refers to one of exactly two kinds of
                account, and the difference between them explains almost everything about how attacks on this
                platform work.
            </Prose>
            <FactGrid items={[
                ['Externally Owned Account (EOA)', 'Controlled by a private key. A person (or a script holding a key) creates and signs transactions from an EOA. EOAs hold ETH but no code.'],
                ['Contract Account', 'Controlled by code, not a key. It has no private key at all; it can only act in response to a transaction or message call that reaches it, and it runs exactly the bytecode deployed at its address.'],
            ]} />
        </StageSection>
    )),

    content('only-eoas-initiate', () => (
        <StageSection title="Only a human (or a key) can press go">
            <Prose>
                A contract can call another contract, transfer ETH, or update its own storage, but only in
                response to being triggered. Nothing ever runs on Ethereum spontaneously. Every chain of execution,
                no matter how many contracts it hops through, traces back to a transaction signed by an EOA. This is
                why "who is the caller?" is one of the first questions to ask when auditing any function.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('keys-to-addresses', () => (
        <StageSection title="From a random number to an address">
            <Prose>
                An Ethereum private key is just a random 256-bit number. Elliptic-curve cryptography (secp256k1,
                the same curve Bitcoin uses) derives a public key from it, and the address is the last 20 bytes of
                the Keccak-256 hash of that public key. The whole chain is one-directional: it's computationally
                infeasible to go from an address back to the private key that produced it.
            </Prose>
            <KeyPoints items={[
                <>Whoever holds the private key can produce a valid signature for that address. There is no password reset.</>,
                <>A wallet is really just software that stores a private key and builds/signs transactions with it.</>,
                <>Losing the key means losing the account permanently; leaking the key means anyone can drain it immediately.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('nonce', () => (
        <StageSection title="The nonce: an account's transaction counter">
            <Prose>
                Every account (EOA or contract) has a <strong>nonce</strong>, a simple integer that starts at zero
                and increments by one every time that account sends a transaction (for contracts, every time it
                deploys another contract). The network requires transactions from an account to use its exact next
                nonce, in order.
            </Prose>
            <Prose>
                That single rule buys two guarantees at once: it stops the exact same signed transaction from being
                replayed twice, and it forces an account's own transactions into a strict, unambiguous order, even
                if they arrive at different nodes at different times.
            </Prose>
            <StageDivider />
            <Prose>
                Contract accounts don't have keys, but they still have addresses, ETH balances, storage, and (as
                you'll see in Stage 5) deployed bytecode that runs inside the EVM whenever a transaction reaches
                them.
            </Prose>
        </StageSection>
    )),

    question(2),
];
